import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useApolloClient } from '@apollo/client';
import { GET_TOKEN_WITH_POSITION } from '../graphql/queries/tokens';
import PositionDetails from '../components/PositionDetails';
import TokenDetails from '../components/TokenDetails';
import ExitStrategyModal from '../components/ExitStrategyModal';
import TransactionsHeader from '../components/TransactionsHeader';
import TransactionModal from '../components/TransactionModal';
import TransactionList from '../components/TransactionList';
// import TechnicalIndicators from '../components/TechnicalIndicators';
import ExitStrategySection from '../components/ExitStrategySection';
import InvalidationStrategySection from '../components/InvalidationStrategySection';
// Removed unused ExitStrategyPlanner
import TokenPriceChart from '../components/TokenPriceChart';
import TokenSentiment from '../components/TokenSentiment';
// TransactionType moved into TransactionsHeader
import { parseFloatFromString } from '../utils/formatters';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { usePositionContext } from '../contexts/PositionContext';
import { useImportModal } from '../contexts/ImportModalContext';
import { isEphemeralTokenId } from '../utils/tokenId';
import { tokenRouteIdentifier, tokenRoutePath } from '../utils/tokenRoutes';
import toast from 'react-hot-toast';

const TokenPage = () => {
  const { coingeckoSlug } = useParams();
  const { currentPortfolioId, currentPortfolio } = usePortfolioContext();
  const { reloadPositions } = usePositionContext();
  const client = useApolloClient();
  const navigate = useNavigate();
  const { openImportModal } = useImportModal();

  const tokenRef = useRef(null);

  // Helper function to get stored collapse state
  const getStoredCollapseState = (sectionName, defaultValue = false) => {
    try {
      const storedValue = localStorage.getItem(`${sectionName}_collapsed`);
      return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    } catch (e) {
      console.warn(`Error retrieving collapse state for ${sectionName}:`, e);
      return defaultValue;
    }
  };

  // Initialize states with localStorage values if available, otherwise use defaults
  const [sentimentCollapsed, setSentimentCollapsed] = useState(() => 
    getStoredCollapseState('sentiment', false)
  );
  const [transactionsCollapsed, setTransactionsCollapsed] = useState(() => 
    getStoredCollapseState('transactions', false)
  );
  
  // Update localStorage when states change
  useEffect(() => {
    localStorage.setItem('sentiment_collapsed', JSON.stringify(sentimentCollapsed));
  }, [sentimentCollapsed]);

  useEffect(() => {
    localStorage.setItem('transactions_collapsed', JSON.stringify(transactionsCollapsed));
  }, [transactionsCollapsed]);

  // Exit Strategy modal state and global open handler (must be before early returns)
  const [exitStrategyModalOpen, setExitStrategyModalOpen] = useState(false);
  const [addTxModalOpen, setAddTxModalOpen] = useState(false);
  const openExitStrategyModal = () => setExitStrategyModalOpen(true);
  const closeExitStrategyModal = () => setExitStrategyModalOpen(false);

  const { loading, error, data, refetch } = useQuery(GET_TOKEN_WITH_POSITION, {
    variables: { coingeckoSlug, portfolioId: currentPortfolioId },
    skip: !currentPortfolioId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    returnPartialData: false
  });

  useEffect(() => {
    tokenRef.current = data?.tokenWithPosition?.token ?? null;
  }, [data?.tokenWithPosition?.token]);

  const openAddTransactionModal = useCallback(() => {
    setAddTxModalOpen(true);
  }, []);

  useEffect(() => {
    const handler = () => openExitStrategyModal();
    const addTxHandler = () => openAddTransactionModal();
    if (typeof window !== 'undefined') {
      window.addEventListener('open-exit-strategy', handler);
      window.addEventListener('open-add-transaction', addTxHandler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('open-exit-strategy', handler);
        window.removeEventListener('open-add-transaction', addTxHandler);
      }
    };
  }, [openAddTransactionModal]);

  // Refetch data when portfolio changes
  useEffect(() => {
    if (currentPortfolioId && coingeckoSlug) {
      refetch({ coingeckoSlug, portfolioId: currentPortfolioId });
    }
  }, [currentPortfolioId, coingeckoSlug, refetch]);

  // Refetch when portfolio currency changes to refresh scaled values (tooltips, PnL)
  useEffect(() => {
    const cur = currentPortfolio?.currency;
    if (currentPortfolioId && coingeckoSlug && cur) {
      refetch({ coingeckoSlug, portfolioId: currentPortfolioId });
    }
  }, [currentPortfolio?.currency, currentPortfolioId, coingeckoSlug, refetch]);

  // Check if we need to redirect to the correct slug
  // Only redirect for persisted tokens (not ephemeral) to avoid redirect loops
  useEffect(() => {
    if (data?.tokenWithPosition?.token && !loading) {
      const token = data.tokenWithPosition.token;
      
      // Don't redirect ephemeral tokens - they can only be fetched by apiId
      // Redirecting to slug would cause a loop since slug isn't searchable
      if (isEphemeralTokenId(token.id)) {
        return;
      }
      
      // For persisted tokens, redirect to the canonical CoinGecko web slug if URL doesn't match.
      // API IDs stay valid as fallback/legacy identifiers, but user-facing links should
      // mirror CoinGecko web pages one-to-one.
      const canonicalIdentifier = tokenRouteIdentifier(token);
      if (canonicalIdentifier && canonicalIdentifier !== coingeckoSlug) {
        console.log(`Redirecting from ${coingeckoSlug} to ${canonicalIdentifier}`);
        navigate(tokenRoutePath(token), { replace: true });
      }
    }
  }, [data, loading, coingeckoSlug, navigate]);

  // Show loading state while waiting for portfolio to be loaded
  if (!currentPortfolioId) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      <span className="ml-3 text-gray-600 dark:text-gray-400">Loading portfolio...</span>
    </div>
  );

  // Show loading only on initial load; keep showing data during background refetches
  if (loading && !data) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );
  
  // Check if this is a "token not found" error (check GraphQL error extensions)
  const isTokenNotFound = error?.graphQLErrors?.some(
    e => e?.extensions?.code === 'TOKEN_NOT_FOUND'
  );
  
  if (isTokenNotFound) return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">Token not found</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        No token matching "<span className="font-mono">{coingeckoSlug}</span>"
      </p>
      <div className="flex gap-3">
        <button
          onClick={() => window.dispatchEvent(new Event('open-search'))}
          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded transition-colors"
        >
          Search
        </button>
        <Link 
          to="/positions" 
          className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold py-2 px-4 rounded transition-colors"
        >
          Positions
        </Link>
      </div>
    </div>
  );
  
  if (error) return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong className="font-bold">Error!</strong>
      <span className="block sm:inline"> {error.message}</span>
    </div>
  );

  if (!data?.tokenWithPosition?.token) return (
    <div className="text-center p-8">
      <h2 className="text-xl font-semibold mb-4">Token not found</h2>
      <Link to="/positions" className="text-blue-500 hover:underline">Return to positions</Link>
    </div>
  );

  const { token, position } = data.tokenWithPosition;
  const tokenLocations = data.tokenLocations || [];
  
  // Additional safety check for token
  if (!token) {
    return (
      <div className="text-center p-8">
        <h2 className="text-xl font-semibold mb-4">Token data not available</h2>
        <Link to="/positions" className="text-blue-500 hover:underline">Return to positions</Link>
      </div>
    );
  }
  
  const currentPrice = token.marketData ? 
    parseFloat(token.marketData.currentPrice.replace('$', '').replace(',', '')) : 
    0;

  // Header summary moved to TransactionsHeader component


  // Calculate planned ROI from exit strategy levels
  // Removed unused calculatePlannedROI

  // Multiple is already displayed at the end; avoid duplicating in the intro section


  // Get latest sentiment data for collapsed header display
  const getLatestSentiment = (sentimentHistory) => {
    if (!sentimentHistory || sentimentHistory.length === 0) {
      return null;
    }
    
    // Sentiment history should already be sorted by timestamp (newest first)
    const latest = sentimentHistory[0];
    return {
      score: latest.score,
      timestamp: latest.timestamp,
      description: latest.description
    };
  };

  const latestSentiment = getLatestSentiment(token?.sentimentHistory);

  // Create cache update function to pass to components instead of refetch
  const updateCache = (updateFunction) => {
    const variables = { coingeckoSlug, portfolioId: currentPortfolioId };
    updateFunction(client.cache, variables);
  };

  // (moved earlier)

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2">
      {/* Responsive layout: md -> details & position side-by-side, chart below; lg+ -> half stats, half chart */}
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-md mb-4 overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3">
          {/* Token details */}
          <div className="lg:col-span-1 lg:col-start-1 lg:row-start-1">
            <TokenDetails token={token} position={position} refetch={refetch} />
          </div>
          {/* Position details */}
          <div className="lg:col-span-1 lg:col-start-1 lg:row-start-2">
            <PositionDetails 
              position={position} 
              token={token}
            />
          </div>
          {/* Chart: full width under on md; right half spanning rows on lg+ */}
          <div className="md:col-span-2 lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:row-span-2">
            <TokenPriceChart 
              tokenId={token.id}
              tokenTicker={token.ticker || 'Unknown'}
              chartSymbol={token.chartSymbol}
              averageBuyPrice={position?.averageBuyPrice}
              averageSellPrice={position?.averageSellPrice}
              transactions={position?.transactions || []}
              plannedLevels={position?.exitStrategy?.levels || []}
              onChartSymbolUpdate={refetch}
              holdingsAmount={position?.holdingsAmount}
              priceHistory={token.priceHistory || []}
              riskScoreHistory={token.riskScoreHistory || []}
              portfolioCurrency={currentPortfolio?.currency || 'USD'}
            />
          </div>
        </div>
      </div>

      <ExitStrategyModal 
        isOpen={exitStrategyModalOpen}
        onClose={closeExitStrategyModal}
        position={position}
        token={token}
        portfolioId={currentPortfolioId}
        updateCache={updateCache}
        refetch={refetch}
      />

      {/* Position Details moved into left column under TokenDetails */}
      
      {/* Market Sentiment Component - Always displayed */}
      {false && <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-4 overflow-hidden">
        <div 
          className="flex justify-between items-center py-2 px-3 cursor-pointer bg-gray-50 dark:bg-gray-700"
          onClick={() => setSentimentCollapsed(!sentimentCollapsed)}
        >
          <h2 className="font-semibold text-base">
            Market Sentiment
            {sentimentCollapsed && latestSentiment && (
              <span className="text-sm font-normal ml-2" title="Latest sentiment analysis">
                <span 
                  className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    latestSentiment.score >= 7 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    latestSentiment.score >= 4 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}
                >
                  {latestSentiment.score}/10
                </span>
                <span className="text-gray-500 dark:text-gray-400 ml-2">
                  {new Date(latestSentiment.timestamp).toLocaleDateString()}
                </span>
              </span>
            )}
          </h2>
          <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            {sentimentCollapsed ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>
        
        {!sentimentCollapsed && (
          <TokenSentiment
            ticker={token.ticker || 'Unknown'}
            tokenId={token.id}
            sentimentHistory={token.sentimentHistory}
          />
        )}
      </div>}
      
      {/* Exit Strategy Section - show when position has holdings */}
      {parseFloatFromString(position?.holdingsAmount) > 0 && (
        <ExitStrategySection
          position={position}
          token={token}
          portfolioId={currentPortfolioId}
          updateCache={updateCache}
          refetch={refetch}
          onEdit={openExitStrategyModal}
          currency={currentPortfolio?.currency}
        />
      )}

      {/* Invalidation Strategy Section - show when position has holdings */}
      {parseFloatFromString(position?.holdingsAmount) > 0 && (
        <InvalidationStrategySection
          position={position}
          token={token}
          portfolioId={currentPortfolioId}
          refetch={refetch}
        />
      )}
      
      {/* Technical Indicators */}
      {/* <TechnicalIndicators indicators={token.technicalIndicators} /> */}
      
      {/* Transactions */}
      {position?.transactions?.length > 0 ? (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-md mb-4 overflow-hidden border border-gray-200 dark:border-gray-700">
          <TransactionsHeader 
            transactions={position?.transactions || []}
            collapsed={transactionsCollapsed}
            onToggle={() => setTransactionsCollapsed(!transactionsCollapsed)}
            onAdd={() => openAddTransactionModal()}
          />
          {!transactionsCollapsed && (
            <div className="p-3">
              <TransactionList 
                transactions={position?.transactions || []}
                token={token}
                currentPrice={currentPrice}
                tokenId={token.id}
                updateCache={updateCache}
                portfolioId={currentPortfolioId}
                holdingsAmount={position?.holdingsAmount}
                refetch={refetch}
                tokenLocations={tokenLocations}
                locationBalances={position?.locationBalances || []}
                onTransactionsChanged={() => {
                  try { reloadPositions(); } catch (_) {}
                }}
                onTransactionsMoved={(result, targetToken) => {
                  const count = result?.movedCount || 1;
                  const name = targetToken?.name || targetToken?.ticker || 'target token';
                  const path = tokenRoutePath(targetToken);
                  toast.custom((t) => (
                    <div
                      className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-sm w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto ring-1 ring-black/5 dark:ring-white/10 overflow-hidden ${path ? 'cursor-pointer' : ''}`}
                      onClick={() => { toast.dismiss(t.id); if (path) navigate(path); }}
                    >
                      <div className="p-4 flex items-start gap-3">
                        <span className="mt-0.5 text-green-500">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Moved {count} transaction{count === 1 ? '' : 's'} to {name}
                          </p>
                          {path && (
                            <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">Click to view &rarr;</p>
                          )}
                        </div>
                        <button
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                          onClick={(e) => { e.stopPropagation(); toast.dismiss(t.id); }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    </div>
                  ), { duration: 5000 });
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="mb-4 py-2 flex items-center justify-center">
          <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-300">
            <button
              type="button"
              className="bg-blue-500 hover:bg-blue-700 text-white text-sm font-semibold py-2 px-4 rounded"
              onClick={() => openAddTransactionModal()}
            >
              {`add ${token.ticker || 'token'} transaction`}
            </button>
            <span>or</span>
            <button
              type="button"
              className="text-blue-600 dark:text-blue-400 underline"
              onClick={() => openImportModal({ portfolioId: currentPortfolioId, onSuccess: () => { try { refetch({ coingeckoSlug, portfolioId: currentPortfolioId }); } catch (_) {} try { reloadPositions(); } catch (_) {} } })}
            >
              use batch import
            </button>
          </div>
        </div>
      )}
      {/* Add Transaction Modal at page level */}
      <TransactionModal 
        isOpen={addTxModalOpen}
        onClose={() => setAddTxModalOpen(false)}
        tokenId={token.id}
        currentPrice={currentPrice}
        updateCache={updateCache}
        portfolioId={currentPortfolioId}
        holdingsAmount={position?.holdingsAmount}
        tokenLocations={tokenLocations}
        locationBalances={position?.locationBalances || []}
        refetch={refetch}
        onSaved={() => {
          try { reloadPositions(); } catch (_) {}
        }}
      />
    </div>
  );
};

export default TokenPage; 